/**
 * LeaseReviewPDFViewer Hook for Lease Review POC
 *
 * Handles PDF rendering with highlight overlay functionality.
 * Uses PDF.js from CDN to render PDF documents and provide text highlighting.
 */
const CONTAINER_PADDING = 32
const HIGHLIGHT_X_OFFSET = -3
const HIGHLIGHT_Y_OFFSET = -4
const HIGHLIGHT_WIDTH_ADDITION = 6
const HIGHLIGHT_HEIGHT_ADDITION = 8

export const LeaseReviewPDFViewer = {
  mounted() {
    this.pdfDocument = null
    this.currentPage = 1
    this.totalPages = 0
    this.scale = 1.0
    this.highlights = []
    // One DOM anchor per match (NOT per overlay element). cycleHighlight
    // scrolls to matchAnchors[index], so cycling 0..(N-1) navigates through
    // distinct matches even when each match produced multiple background
    // bboxes or underline elements. Without this the badge's "X of Y" index
    // would walk through bboxes within a single match and never reach the
    // later matches.
    this.matchAnchors = []
    this.markedWords = []
    this.currentHighlightIndex = 0
    this.currentHighlightedField = null
    this.pageCanvases = {} // Store canvas elements by page number
    this.pageViewports = {} // Store viewports by page number
    this.intersectionObserver = null
    this.syncDebugState()

    // Wait for PDF.js to load from CDN
    this.waitForPdfJs()
      .then(() => {
        this.initializeLeaseReviewPDFViewer()
      })
      .catch((error) => {
        console.error("Failed to load PDF.js:", error)
        this.showError("Failed to load PDF viewer")
      })

    // Listen for highlight events from LiveView
    this.handleEvent("highlight_text", (data) => {
      this.highlightText(data)
    })

    this.handleEvent("clear_highlights", (data) => {
      this.clearHighlights()
    })

    this.handleEvent("cycle_highlight", (data) => {
      this.cycleHighlight(data)
    })

    this.handleEvent("preview_mark_words", (data) => {
      this.markWords(data)
    })

    this.handleEvent("preview_clear_words", (data) => {
      this.clearMarkedWords()
    })
  },

  destroyed() {
    if (this.pdfDocument) {
      this.pdfDocument.destroy()
    }
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect()
    }
  },

  /**
   * Wait for PDF.js library to be available
   */
  async waitForPdfJs() {
    return new Promise((resolve, reject) => {
      if (window.pdfjsLib) {
        resolve()
        return
      }

      let attempts = 0
      const maxAttempts = 50 // 5 seconds timeout

      const checkPdfJs = () => {
        if (window.pdfjsLib) {
          resolve()
        } else if (attempts >= maxAttempts) {
          reject(new Error("PDF.js failed to load"))
        } else {
          attempts++
          setTimeout(checkPdfJs, 100)
        }
      }

      checkPdfJs()
    })
  },

  /**
   * Initialize the PDF viewer
   */
  async initializeLeaseReviewPDFViewer() {
    const pagesContainer = this.el.querySelector("#pdf-pages-container > div")

    if (!pagesContainer) {
      console.error("PDF pages container not found")
      return
    }

    this.pagesContainer = pagesContainer

    // Get PDF path from component data attribute
    const pdfPath = this.el.dataset.pdfPath

    try {
      await this.loadPDF(pdfPath)
      this.showStatus("PDF loaded successfully")
    } catch (error) {
      this.handlePdfLoadError(error)
    }
  },

  /**
   * Translate a PDF.js / fetch failure into a user-safe message and report
   * it to the server so it surfaces in AppSignal. The raw exception message
   * from PDF.js includes the full signed URL — never render it.
   */
  handlePdfLoadError(error) {
    const status = typeof error?.status === "number" ? error.status : null
    const errorName = error?.name || "Error"

    console.error("PDF Viewer Error:", errorName, status || "")

    this.showError(this.el.dataset.errorMessage)

    // Only report to the server when mounted under a LiveComponent that
    // handles "pdf_load_failed" (extraction review). The admin PDF inspector
    // mounts this hook on a plain LiveView with no handler, so skip there
    // to avoid unhandled-event warnings.
    if (this.el.getAttribute("phx-target")) {
      this.pushEventTo(this.el, "pdf_load_failed", {
        error_name: errorName,
        status: status,
      })
    }
  },

  /**
   * Load and render PDF document
   */
  async loadPDF(pdfPath) {
    const loadingTask = window.pdfjsLib.getDocument({
      url: pdfPath,
      cMapUrl: "https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/cmaps/",
      cMapPacked: true,
    })

    this.pdfDocument = await loadingTask.promise
    this.totalPages = this.pdfDocument.numPages
    console.debug(`PDF loaded: ${this.totalPages} pages`)

    // Update page indicator
    this.updatePageIndicator(1)

    // Render all pages
    await this.renderAllPages()

    // Setup scroll tracking
    this.setupScrollTracking()
  },

  /**
   * Render all PDF pages
   */
  async renderAllPages() {
    if (!this.pdfDocument) return

    // Calculate scale based on container width (use first page as reference)
    const firstPage = await this.pdfDocument.getPage(1)
    const containerWidth = this.pagesContainer.clientWidth - CONTAINER_PADDING // Account for padding
    const initialViewport = firstPage.getViewport({ scale: 1.0 })
    this.scale = containerWidth / initialViewport.width

    console.debug(`Rendering ${this.totalPages} pages with scale ${this.scale}`)

    // Drop any placeholder/loading content before appending real pages so it
    // doesn't bleed through behind the rendered canvases.
    this.pagesContainer.replaceChildren()

    // Render each page
    for (let pageNum = 1; pageNum <= this.totalPages; pageNum++) {
      await this.renderPage(pageNum)
    }
  },

  /**
   * Render a specific page
   */
  async renderPage(pageNumber) {
    if (!this.pdfDocument) return

    const page = await this.pdfDocument.getPage(pageNumber)

    // Get viewport with calculated scale
    const viewport = page.getViewport({ scale: this.scale })

    // Create page wrapper
    const pageWrapper = document.createElement("div")
    pageWrapper.id = `pdf-page-${pageNumber}`
    pageWrapper.className = "relative bg-white rounded-[5px] shadow-lg"
    pageWrapper.style.width = `${viewport.width}px`

    // Create canvas for this page
    const canvas = document.createElement("canvas")
    canvas.id = `pdf-canvas-${pageNumber}`
    canvas.className = "rounded-[5px]"
    canvas.width = viewport.width
    canvas.height = viewport.height
    canvas.style.display = "block"

    // Create highlight container for this page
    const highlightContainer = document.createElement("div")
    highlightContainer.id = `pdf-highlights-${pageNumber}`
    highlightContainer.className = "absolute inset-[0px] pointer-events-none"

    // Create marked words container for this page
    const markedWordsContainer = document.createElement("div")
    markedWordsContainer.id = `pdf-marked-words-${pageNumber}`
    markedWordsContainer.className = "absolute inset-[0px] cursor-pointer"
    markedWordsContainer.addEventListener("click", (event) => {
      if (event.target.classList.contains("pdf-bbox-container")) {
        this.pushEvent("select_mark_word", {
          document_order: event.target.dataset.documentOrder,
          source: event.target.dataset.source,
        })
      }
    })

    // Assemble page structure
    pageWrapper.appendChild(canvas)
    pageWrapper.appendChild(highlightContainer)
    pageWrapper.appendChild(markedWordsContainer)
    this.pagesContainer.appendChild(pageWrapper)

    // Store references
    this.pageCanvases[pageNumber] = canvas
    this.pageViewports[pageNumber] = viewport

    // Render page
    const context = canvas.getContext("2d")
    const renderContext = {
      canvasContext: context,
      viewport: viewport,
    }

    await page.render(renderContext).promise
    console.debug(`Rendered page ${pageNumber}`)
  },

  markWords(data) {
    data.words.forEach((coord, index) =>
      this.markWordBBox(
        coord,
        index,
        data.color_class,
        data.display_word_order,
      ),
    )
  },

  markWordBBox(coord, index, colorClass, displayWordOrder) {
    const pageNum = coord.page || 1
    const viewport = this.pageViewports[pageNum]
    const canvas = this.pageCanvases[pageNum]
    const markedWordsContainer = document.getElementById(
      `pdf-marked-words-${pageNum}`,
    )

    if (!viewport || !canvas || !markedWordsContainer) {
      console.error(`Page ${pageNum} not found for marking words`)
      return
    }

    // Use the actual displayed canvas dimensions
    const canvasRect = {
      width: canvas.clientWidth,
      height: canvas.clientHeight,
    }

    const canvasCoords = this.convertPDFToCanvasCoordinates(
      coord,
      canvasRect,
      viewport,
    )

    // Create main bbox container
    const bboxElement = document.createElement("div")
    bboxElement.className = `pdf-bbox-container ${colorClass || ""}`
    bboxElement.style.cssText = `
      position: absolute;
      cursor: pointer;
      left: ${canvasCoords.x}px;
      top: ${canvasCoords.y}px;
      width: ${Math.max(canvasCoords.width, 2)}px;
      height: ${Math.max(canvasCoords.height, 2)}px;
      box-sizing: border-box;
      z-index: 10;
    `
    if (displayWordOrder) {
      const orderLabel = document.createElement("span")
      orderLabel.innerText = coord.document_order
      orderLabel.style.cssText = `
      font-size: 0.625rem /* root-scale: target */;
      background-color: black;
      color: white;
    `
      bboxElement.appendChild(orderLabel)
    }

    bboxElement.dataset.documentOrder = coord.document_order
    bboxElement.dataset.source = coord.source
    bboxElement.dataset.index = index

    markedWordsContainer.appendChild(bboxElement)
    this.markedWords.push(bboxElement)

    console.debug(`Created bbox ${index} on page ${pageNum}:`, {
      originalCoord: coord,
      convertedCoords: canvasCoords,
      canvasRect,
    })
  },

  clearMarkedWords() {
    this.markedWords.forEach((word) => {
      word.remove()
    })
    this.markedWords = []
    console.debug("Cleared all marked words")
  },

  /**
   * Highlight text based on coordinates
   */
  highlightText(data) {
    const { coordinates, marked_portion, field_path, context, index } = data
    const parsedIndex = Number.parseInt(index, 10)
    const requestedIndex = Number.isNaN(parsedIndex) ? 0 : parsedIndex

    if (this.currentHighlightedField === field_path) {
      this.currentHighlightIndex = requestedIndex
      this.syncDebugState()
      return
    }

    this.currentHighlightIndex = requestedIndex
    this.currentHighlightedField = field_path || null
    this.syncDebugState()

    // Use coordinates for background highlighting (full context)
    // Use marked_portion for underline highlighting (marked text)
    const background_coords = coordinates || []
    const underline_coords = marked_portion || []

    if (background_coords.length === 0 && underline_coords.length === 0) {
      console.debug("No coordinates provided for highlighting")
      return
    }

    // Determine page from either coordinate set
    const targetPage = (background_coords[0] || underline_coords[0])?.page || 1

    // Clear existing highlights
    this.clearHighlights(false)

    console.debug(
      `Highlighting ${background_coords.length} background regions and ${underline_coords.length} underline regions for field: ${field_path} on page ${targetPage}`,
    )

    // Create background highlights (full context - yellow). Capture the
    // first DOM element produced for each match as that match's anchor —
    // cycleHighlight scrolls there when the badge requests "match N".
    background_coords.forEach((match, matchIdx) => {
      let firstEl = null
      match.coords.forEach((c, index) => {
        const el = this.createHighlightOverlay(c, index)
        if (!firstEl && el) firstEl = el
      })
      if (firstEl) this.matchAnchors[matchIdx] = firstEl
    })

    // Create underline highlights (marked portion - red) on top of background.
    // Fall back to the first underline as the match anchor when the match
    // had no background bboxes (rare, but keeps every match navigable).
    underline_coords.forEach((match, matchIdx) => {
      let firstEl = null
      match.coords.forEach((c, index) => {
        const el = this.createUnderlineOverlay(c, index)
        if (!firstEl && el) firstEl = el
      })
      if (firstEl && !this.matchAnchors[matchIdx]) {
        this.matchAnchors[matchIdx] = firstEl
      }
    })

    if (this.highlights.length > 0) {
      this.currentHighlightIndex = requestedIndex
      this.scrollToCurrentHighlight()
      this.syncDebugState()
    }

    this.currentHighlightedField = field_path

    this.showStatus(
      `Highlighted ${background_coords.length} context regions with ${underline_coords.length} marked portions for: ${field_path}`,
    )
  },

  scrollToCurrentHighlight() {
    const anchor =
      this.matchAnchors[this.currentHighlightIndex] ||
      this.highlights[this.currentHighlightIndex]
    if (anchor) {
      anchor.scrollIntoViewIfNeeded()
    }
  },

  cycleHighlight(data) {
    const parsedIndex = Number.parseInt(data.highlight_index, 10)
    this.currentHighlightIndex = Number.isNaN(parsedIndex) ? 0 : parsedIndex
    this.syncDebugState()
    this.scrollToCurrentHighlight()
  },

  /**
   * Create highlight overlay element
   */
  createHighlightOverlay(coord, index) {
    const pageNum = coord.page || 1
    const viewport = this.pageViewports[pageNum]
    const canvas = this.pageCanvases[pageNum]
    const highlightContainer = document.getElementById(
      `pdf-highlights-${pageNum}`,
    )

    if (!viewport || !canvas || !highlightContainer) {
      console.error(`Page ${pageNum} not found for highlighting`)
      return
    }

    // Use the actual displayed canvas dimensions
    const canvasRect = {
      width: canvas.clientWidth,
      height: canvas.clientHeight,
    }

    const canvasCoords = this.convertPDFToCanvasCoordinates(
      coord,
      canvasRect,
      viewport,
    )

    // Create main highlight container
    const highlightElement = document.createElement("div")
    highlightElement.className = "pdf-highlight-container"
    highlightElement.style.cssText = `
      position: absolute;
      pointer-events: none;
      left: ${canvasCoords.x}px;
      top: ${canvasCoords.y}px;
      width: ${Math.max(canvasCoords.width, 2)}px;
      height: ${Math.max(canvasCoords.height, 2)}px;
      box-sizing: border-box;
      background-color: rgba(255, 235, 59, 0.5);
      border: 1px solid rgba(255, 193, 7, 0.7);
      z-index: 10;
      mix-blend-mode: multiply;
    `
    highlightElement.dataset.index = index

    highlightContainer.appendChild(highlightElement)
    this.highlights.push(highlightElement)

    console.debug(`Created highlight ${index} on page ${pageNum}:`, {
      originalCoord: coord,
      convertedCoords: canvasCoords,
      canvasRect,
    })

    return highlightElement
  },

  /**
   * Create underline overlay element (for marked portions)
   */
  createUnderlineOverlay(coord, index) {
    if (!coord) {
      console.error("No coordinate provided for underline")
      return
    }

    const pageNum = coord.page || 1
    const viewport = this.pageViewports[pageNum]
    const canvas = this.pageCanvases[pageNum]
    const highlightContainer = document.getElementById(
      `pdf-highlights-${pageNum}`,
    )

    if (!viewport || !canvas || !highlightContainer) {
      console.error(`Page ${pageNum} not found for underline`)
      return
    }

    // Use the actual displayed canvas dimensions
    const canvasRect = {
      width: canvas.clientWidth,
      height: canvas.clientHeight,
    }

    const canvasCoords = this.convertPDFToCanvasCoordinates(
      coord,
      canvasRect,
      viewport,
    )

    // Create underline element
    const underlineElement = document.createElement("div")
    underlineElement.className = "pdf-underline-standalone"
    underlineElement.style.cssText = `
      position: absolute;
      pointer-events: none;
      left: ${canvasCoords.x}px;
      top: ${canvasCoords.y}px;
      width: ${Math.max(canvasCoords.width, 2)}px;
      height: ${Math.max(canvasCoords.height, 2)}px;
      border-bottom: 3px solid red;
      box-sizing: border-box;
      z-index: 20;
    `

    highlightContainer.appendChild(underlineElement)
    this.highlights.push(underlineElement)

    console.debug(`Created standalone underline ${index} on page ${pageNum}:`, {
      originalCoord: coord,
      convertedCoords: canvasCoords,
      canvasRect,
    })

    return underlineElement
  },

  /**
   * Convert relative coordinates (0-1) to canvas coordinates
   */
  convertPDFToCanvasCoordinates(coord, canvasRect, viewport) {
    if (!viewport) {
      console.error("No viewport available for coordinate conversion")
      return { x: 0, y: 0, width: 0, height: 0 }
    }

    // Convert relative coordinates (0-1) to absolute viewport coordinates
    const canvasX = coord.x * viewport.width
    const canvasY = coord.y * viewport.height
    const viewportWidth = coord.width * viewport.width
    const viewportHeight = coord.height * viewport.height

    // Apply any additional scaling if canvas is CSS-scaled
    const cssScaleX = canvasRect.width / viewport.width
    const cssScaleY = canvasRect.height / viewport.height

    const result = {
      x: canvasX * cssScaleX + HIGHLIGHT_X_OFFSET,
      y: canvasY * cssScaleY + HIGHLIGHT_Y_OFFSET,
      width: viewportWidth * cssScaleX + HIGHLIGHT_WIDTH_ADDITION,
      height: viewportHeight * cssScaleY + HIGHLIGHT_HEIGHT_ADDITION,
    }

    return result
  },

  /**
   * Clear all highlights
   */
  clearHighlights(resetIndex = true) {
    this.highlights.forEach((highlight) => {
      highlight.remove()
    })
    this.highlights = []
    this.matchAnchors = []
    if (resetIndex) {
      this.currentHighlightIndex = 0
    }
    this.currentHighlightedField = null
    this.syncDebugState()
    console.debug("Cleared all highlights")
  },

  syncDebugState() {
    if (!this.el?.dataset) return

    this.el.dataset.currentHighlightIndex = String(
      this.currentHighlightIndex ?? 0,
    )
    this.el.dataset.currentHighlightedField = this.currentHighlightedField || ""
  },

  /**
   * Show status message
   */
  showStatus(message) {
    console.debug("PDF Viewer:", message)
    // Could show in UI status area if needed
  },

  /**
   * Show error message
   */
  showError(message) {
    console.error("PDF Viewer Error:", message)
    // Could show in UI error area if needed

    // Show simple error in PDF container
    const errorDiv = document.createElement("div")
    errorDiv.className = "pdf-error"
    errorDiv.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(239, 68, 68, 0.1);
      border: 1px solid rgba(239, 68, 68, 0.3);
      padding: 10px /* root-scale: legacy-px */;
      border-radius: 3.75px /* root-scale: legacy-px */;
      text-align: center;
      color: rgba(239, 68, 68, 0.9);
    `
    errorDiv.textContent = message

    this.el.appendChild(errorDiv)
  },

  /**
   * Setup scroll tracking with Intersection Observer
   */
  setupScrollTracking() {
    const options = {
      root: this.el.querySelector("#pdf-pages-container"),
      threshold: 0.5, // Consider page visible when 50% is in viewport
    }

    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // Extract page number from element ID (pdf-page-1, pdf-page-2, etc.)
          const pageNum = parseInt(entry.target.id.replace("pdf-page-", ""), 10)
          if (!isNaN(pageNum) && pageNum !== this.currentPage) {
            this.currentPage = pageNum
            this.updatePageIndicator(pageNum)
          }
        }
      })
    }, options)

    // Observe all page wrappers
    for (let pageNum = 1; pageNum <= this.totalPages; pageNum++) {
      const pageWrapper = document.getElementById(`pdf-page-${pageNum}`)
      if (pageWrapper) {
        this.intersectionObserver.observe(pageWrapper)
      }
    }
  },

  /**
   * Update page number indicator
   */
  updatePageIndicator(pageNum) {
    const indicator = document.getElementById("pdf-page-indicator")
    if (indicator) {
      indicator.textContent = `Page ${pageNum} / ${this.totalPages}`
    }
  },

  /**
   * Scroll to a specific page with smooth animation
   */
  scrollToPage(pageNum) {
    const pageWrapper = document.getElementById(`pdf-page-${pageNum}`)
    if (pageWrapper) {
      pageWrapper.scrollIntoView({
        behavior: "smooth",
        block: "center",
      })
      console.debug(`Scrolled to page ${pageNum}`)
    }
  },
}
